// Extracted from original/src/App.tsx:38816-40760 (the report windows of section 12, ADR-08 §2.1 "copy, then cut logic"):
//   shift report "تقرير مطابقة الوردية والتسوية التفصيلي والكلي" — window, page 1 (identity, metadata grid,
//   reconciliation table, summary cards, notes, three signatures, footer), page 2 (appendix header, operations table,
//   two signatures, footer), bottom bar (PDF / print / close)                                     :38816-39760
//   daily report "تقرير تسوية ومطابقة الأرصدة الشامل لليوم" — admin-only notice, page 1 (gradient header, identity,
//   comparison grid, expected / actual / variance, counts, note), page 2 (operations), page 3 (expenses)   :39762-40760
// Kept: the frames, the page containers (`printable-page` / `printable-report`), headers, badges, table heads, row
// colour rules (debts red when they grow, trusts amber, expenses red, profit green), the summary cards, signature
// blocks, page footers and every class string.
// Cut: every figure the windows computed from browser storage (`getShiftStatistics`, the snapshot arrays, the
// `debt_id` / text matching of the appendix, App.tsx:38818-38935) → the server's report (`GET /shifts/:id/report`,
// `GET /days/:id/report`: balances at the shift's chain positions / the office day, the drawer's own operations),
// passed in as preformatted rows; `generatePDF` / `printElement` → `onPdf` / `onPrint` (the container renders the same
// pages in the print root and prints through @faylasuf/print, D-146).
// Changed by decision: the company name is the office's identity, not the hard-coded "شركة الفيلسوف للصيرفة والتحويل
// المالي" (F-286 pattern); the appendix lists the shift's **own** operations only (D-122; old: every operator's within
// ±60 s, R-S10) in one table — the old four sections (debts / trusts / box transfers / others) split movements by the
// customer-text heuristics the new ledger does not need (V-xx in REPORT_C32.md); "المصروفات التشغيلية للوردية" holds
// expenses only and other payments get their own row (D-122); the opening-adjustments table becomes the table of cash
// counts and their approval state (D-120, D-299); profits per currency (D-098).
import { motion } from 'motion/react';
import { Download, Printer, Shield, X } from 'lucide-react';
import { useI18n } from '../i18n-context.js';

export interface ReportCell {
  readonly text: string;
  readonly cls?: string;
}

export interface ReportRow {
  readonly key: string;
  readonly name: string;
  readonly rowCls?: string;
  readonly nameCls?: string;
  readonly opening: readonly ReportCell[];
  readonly closing: readonly ReportCell[];
  readonly variance: readonly ReportCell[];
}

export interface ReportCountLine {
  readonly key: string;
  readonly scope: string;
  readonly expected: string;
  readonly counted: string;
  readonly difference: string;
  readonly negative: boolean;
  readonly status: string;
}

export interface ReportOperation {
  readonly key: string;
  readonly typeLabel: string;
  readonly timeLabel: string;
  readonly description: string;
  readonly inflow: boolean;
  readonly amountLabel: string;
  readonly correction: boolean;
}

export interface ShiftReportData {
  readonly shiftNo: string;
  readonly owner: string;
  readonly open: boolean;
  readonly openedLabel: string;
  readonly closedLabel: string | null;
  readonly generatedLabel: string;
  readonly officeName: string;
  readonly rows: readonly ReportRow[];
  readonly theo: { readonly iqd: string; readonly usd: string };
  readonly actual: { readonly iqd: string; readonly usd: string };
  readonly diff: { readonly zero: boolean; readonly iqd: string; readonly usd: string | null };
  readonly counts: readonly ReportCountLine[];
  readonly note: string | null;
  readonly operations: readonly ReportOperation[];
  readonly copy: boolean;
}

const countTable = (counts: readonly ReportCountLine[], t: (k: string) => string) =>
  counts.length > 0 ? (
    <div className="space-y-2">
      <h4 className="text-xs font-black text-slate-700 dark:text-slate-300">{t('mt.rep_counts_title')}</h4>
      <div className="overflow-hidden border border-slate-200 dark:border-slate-800 rounded-xl">
        <table className="w-full text-right border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50 dark:bg-[#161b22] text-slate-600 border-b border-slate-200 text-[10px] font-black">
              <th className="p-2 text-right">{t('mt.pcol_scope')}</th>
              <th className="p-2 text-center">{t('mt.pcol_expected')}</th>
              <th className="p-2 text-center">{t('mt.pcol_counted')}</th>
              <th className="p-2 text-left">{t('mt.pcol_difference')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-mono">
            {counts.map((c) => (
              <tr key={c.key}>
                <td className="p-2 font-sans font-bold">{c.scope}</td>
                <td className="p-2 text-center">{c.expected}</td>
                <td className="p-2 text-center">{c.counted}</td>
                <td className={`p-2 text-left font-bold ${c.negative ? 'text-rose-600' : 'text-primary-600'}`}>
                  {c.difference} <span className="font-sans text-[10px] text-slate-400">{c.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

const cells = (list: readonly ReportCell[]) => list.map((c, i) => <div key={i} className={c.cls}>{c.text}</div>);

/** The two A4 pages of the shift report (the window and the print root render the same pages). */
export function ShiftReportPages({ d }: { d: ShiftReportData }) {
  const { t, locale } = useI18n();
  return (
    <div className="space-y-6" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {/* PAGE 1: التقرير الرئيسي ومطابقة الأرصدة والتسوية الشاملة للوردية */}
      <div className="printable-page bg-white dark:bg-[#0d1117] rounded-3xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-5">
        <div className="border-b-2 border-slate-800 dark:border-slate-200 pb-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div className="space-y-1 text-right">
              <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{d.officeName}</h1>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-black">{t('mt.rep_sheet_badge')}</span>
              <span className={`px-3 py-1 rounded-xl text-xs font-black ${d.open ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}>
                {d.open ? t('mt.rep_active') : t('mt.rep_closed')}
              </span>
              {d.copy && <span className="px-3 py-1 rounded-xl text-xs font-black bg-amber-100 text-amber-700">{t('mt.rep_copy')}</span>}
            </div>
          </div>
          {/* Metadata Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-800/60 text-xs">
            <div className="bg-slate-50 dark:bg-[#161b22] p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-400 block text-[10px] font-bold">{t('mt.rep_shift_id')}</span>
              <span className="font-mono font-black text-amber-600">#{d.shiftNo}</span>
            </div>
            <div className="bg-slate-50 dark:bg-[#161b22] p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-400 block text-[10px] font-bold">{t('mt.rep_operator')}</span>
              <span className="font-black text-slate-800 dark:text-slate-200">{d.owner}</span>
            </div>
            <div className="bg-slate-50 dark:bg-[#161b22] p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-400 block text-[10px] font-bold">{t('mt.rep_open_time')}</span>
              <span className="font-mono font-bold text-slate-700 dark:text-slate-300 text-[11px]">{d.openedLabel}</span>
            </div>
            <div className="bg-slate-50 dark:bg-[#161b22] p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-400 block text-[10px] font-bold">{t('mt.rep_close_time')}</span>
              <span className="font-mono font-bold text-slate-700 dark:text-slate-300 text-[11px]">{d.closedLabel ?? t('mt.rep_ongoing')}</span>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-black text-slate-800 dark:text-white flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-primary-600"></span>
            {t('mt.rep_table_title')}
          </h3>
          <span className="text-[11px] font-mono text-slate-400">
            {t('mt.rep_generated')} {d.generatedLabel}
          </span>
        </div>
        <div className="overflow-hidden border border-slate-300 dark:border-[#30363d] rounded-2xl">
          <table className="w-full text-right border-collapse text-xs">
            <thead>
              <tr className="bg-slate-100 dark:bg-[#161b22] text-slate-700 dark:text-[#8b949e] border-b border-slate-300 dark:border-[#30363d] font-black text-[11px] uppercase">
                <th className="p-2.5 text-right">{t('mt.rep_col_account')}</th>
                <th className="p-2.5 text-center">{t('mt.rep_col_opening')}</th>
                <th className="p-2.5 text-center">{t('mt.rep_col_closing')}</th>
                <th className="p-2.5 text-left">{t('mt.rep_col_variance')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-[#30363d] font-mono">
              {d.rows.map((r) => (
                <tr key={r.key} className={`hover:bg-slate-50/60 dark:hover:bg-[#161b22]/30 ${r.rowCls ?? ''}`} data-testid="report-row">
                  <td className={`p-2.5 text-right font-sans font-bold ${r.nameCls ?? 'text-slate-800 dark:text-slate-200'}`}>{r.name}</td>
                  <td className="p-2.5 text-center text-slate-600 dark:text-slate-300">{cells(r.opening)}</td>
                  <td className="p-2.5 text-center text-slate-800 dark:text-slate-100 font-bold">{cells(r.closing)}</td>
                  <td className="p-2.5 text-left font-black">{cells(r.variance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Summary Variance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          <div className="bg-slate-50 dark:bg-[#161b22] p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 text-center">
            <p className="text-[10px] font-bold text-slate-500 dark:text-[#8b949e] uppercase">{t('mt.rep_theo')}</p>
            <p className="text-base font-black text-slate-800 dark:text-white font-mono mt-1">{d.theo.iqd} د.ع</p>
            <p className="text-xs font-bold text-slate-500 font-mono">${d.theo.usd}</p>
          </div>
          <div className="bg-slate-50 dark:bg-[#161b22] p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 text-center">
            <p className="text-[10px] font-bold text-slate-500 dark:text-[#8b949e] uppercase">{t('mt.rep_actual')}</p>
            <p className="text-base font-black text-primary-600 dark:text-primary-400 font-mono mt-1">{d.actual.iqd} د.ع</p>
            <p className="text-xs font-bold text-slate-500 font-mono">${d.actual.usd}</p>
          </div>
          <div
            className={`p-3.5 rounded-2xl border text-center flex flex-col justify-center ${
              d.diff.zero
                ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400'
                : 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800/40 text-rose-700 dark:text-rose-400'
            }`}
          >
            <p className="text-[10px] font-bold uppercase">{t('mt.rep_result')}</p>
            {d.diff.zero ? (
              <p className="text-sm font-black mt-1">✓ {t('mt.rep_perfect')}</p>
            ) : (
              <div className="text-xs font-black font-mono mt-1">
                <div>{d.diff.iqd} د.ع</div>
                {d.diff.usd && <div className="text-[11px]">{d.diff.usd}</div>}
              </div>
            )}
          </div>
        </div>
        {countTable(d.counts, t)}
        {d.note && (
          <div className="text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-[#161b22] p-3 rounded-xl border border-slate-200 dark:border-slate-800">
            <span className="text-slate-400 block text-[10px]">{t('mt.rep_notes')}</span>"{d.note}"
          </div>
        )}
        {/* Official Signatures Block */}
        <div className="pt-4 border-t-2 border-dashed border-slate-200 dark:border-slate-800 grid grid-cols-3 gap-4 text-center text-xs">
          <div className="space-y-6">
            <p className="font-black text-slate-700 dark:text-slate-300">{t('mt.rep_sig_operator')}</p>
            <div className="border-b-2 border-dotted border-slate-400 mx-4 pb-1 text-[10px] text-slate-400 font-bold">{d.owner}</div>
          </div>
          <div className="space-y-6">
            <p className="font-black text-slate-700 dark:text-slate-300">{t('mt.rep_sig_accountant')}</p>
            <div className="border-b-2 border-dotted border-slate-400 mx-4 pb-1 text-[10px] text-slate-400 font-bold">....................................</div>
          </div>
          <div className="space-y-6">
            <p className="font-black text-slate-700 dark:text-slate-300">{t('mt.rep_sig_management')}</p>
            <div className="border-b-2 border-dotted border-slate-400 mx-4 pb-1 text-[10px] text-slate-400 font-bold">....................................</div>
          </div>
        </div>
        <div className="pt-2 flex justify-between items-center text-[10px] font-bold text-slate-400 border-t border-slate-100 dark:border-slate-800/60">
          <span>{d.officeName}</span>
          <span>{t('mt.rep_page1')}</span>
        </div>
      </div>
      {/* Page Break for Clean Printing */}
      <div className="page-break my-6" />
      {/* PAGE 2: كشف ومطابقة تفصيلية ملحقة بحركات الوردية */}
      <div className="printable-page bg-white dark:bg-[#0d1117] rounded-3xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-5">
        <div className="border-b-2 border-slate-800 dark:border-slate-200 pb-3 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              {t('mt.rep_appendix_title')}
            </h2>
            <p className="text-xs font-bold text-slate-500">{t('mt.rep_appendix_sub')}</p>
          </div>
          <div className="text-left font-mono text-xs font-bold text-slate-600 dark:text-slate-300">
            <span>{t('mt.rep_shift_no', { id: d.shiftNo })}</span>
            <span className="mx-2">•</span>
            <span>{d.owner}</span>
          </div>
        </div>
        <OperationsTable ops={d.operations} />
        <div className="pt-4 border-t-2 border-dashed border-slate-200 dark:border-slate-800 grid grid-cols-2 gap-6 text-center text-xs">
          <div className="space-y-6">
            <p className="font-black text-slate-700 dark:text-slate-300">{t('mt.rep_sig_executor')}</p>
            <div className="border-b-2 border-dotted border-slate-400 mx-8 pb-1 text-[10px] text-slate-400 font-bold">{d.owner}</div>
          </div>
          <div className="space-y-6">
            <p className="font-black text-slate-700 dark:text-slate-300">{t('mt.rep_sig_auditor')}</p>
            <div className="border-b-2 border-dotted border-slate-400 mx-8 pb-1 text-[10px] text-slate-400 font-bold">....................................</div>
          </div>
        </div>
        <div className="pt-2 flex justify-between items-center text-[10px] font-bold text-slate-400 border-t border-slate-100 dark:border-slate-800/60">
          <span>{d.officeName}</span>
          <span>{t('mt.rep_page2')}</span>
        </div>
      </div>
    </div>
  );
}

function OperationsTable({ ops, title }: { ops: readonly ReportOperation[]; title?: string }) {
  const { t } = useI18n();
  return (
    <div className="printable-section space-y-2">
      <div className="flex justify-between items-center pb-1 border-b border-slate-200 dark:border-slate-800">
        <h4 className="text-xs font-black text-slate-800 dark:text-slate-200">{title ?? t('mt.rep_ops_title')}</h4>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">{t('mt.rep_records', { n: ops.length })}</span>
      </div>
      {ops.length === 0 ? (
        <p className="text-center py-4 text-[11px] text-slate-400">{t('mt.rep_no_ops')}</p>
      ) : (
        <div className="overflow-hidden border border-slate-200 dark:border-slate-800 rounded-xl">
          <table className="w-full text-right border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 dark:bg-[#161b22] text-slate-600 dark:text-slate-400 border-b border-slate-200 text-[10px] font-black uppercase">
                <th className="p-2 text-right">{t('mt.rep_col_type')}</th>
                <th className="p-2 text-center">{t('mt.rep_col_time')}</th>
                <th className="p-2 text-right">{t('mt.rep_col_desc')}</th>
                <th className="p-2 text-left">{t('mt.rep_col_amount')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
              {ops.map((m) => (
                <tr key={m.key} className="hover:bg-slate-50/50" data-testid="report-op">
                  <td className="p-2 text-right font-sans font-bold text-slate-800 dark:text-slate-200">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black ${m.inflow ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400' : 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400'}`}>
                      {m.correction ? t('mt.rep_correction') : m.inflow ? t('mt.rep_income') : t('mt.rep_outgo')}
                    </span>
                  </td>
                  <td className="p-2 text-center text-[10px] text-slate-500">{m.timeLabel}</td>
                  <td className="p-2 text-right text-[11px] font-sans text-slate-600 dark:text-slate-300">
                    {m.typeLabel}
                    {m.description ? ` — ${m.description}` : ''}
                  </td>
                  <td className={`p-2 text-left font-black ${m.inflow ? 'text-emerald-600' : 'text-rose-600'}`} dir="ltr">
                    {m.amountLabel}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export interface ReportWindowProps {
  readonly open: boolean;
  readonly busy: boolean;
  readonly onPdf: () => void;
  readonly onPrint: () => void;
  readonly onClose: () => void;
}

export function ShiftReportModal(p: ReportWindowProps & { readonly data: ShiftReportData | null }) {
  const { t } = useI18n();
  if (!p.open || !p.data) return null;
  const d = p.data;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn" data-testid="shift-report-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white dark:bg-[#0d1117] rounded-[2rem] border border-slate-100 dark:border-[#30363d] w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="p-5 border-b border-slate-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#161b22]/30 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-500/10 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 flex items-center justify-center font-black">
              <Printer size={18} />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-tight">{t('mt.rep_shift_title')}</h3>
              <p className="text-xs font-bold text-slate-500">{t('mt.rep_shift_sub', { id: d.shiftNo, name: d.owner })}</p>
            </div>
          </div>
          <button type="button" onClick={p.onClose} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 flex items-center justify-center transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6 space-y-6 printable-report bg-slate-50/50 dark:bg-slate-900/50 text-right">
          <ShiftReportPages d={d} />
        </div>
        <div className="p-5 border-t border-slate-100 dark:border-[#30363d] flex flex-wrap gap-3 bg-gray-50/50 dark:bg-[#161b22]/30 shrink-0">
          <button type="button" disabled={p.busy} onClick={p.onPdf} className="flex-1 min-w-[140px] bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2 shadow-lg shadow-rose-500/10">
            <Download size={16} />
            {t('mt.rep_export_pdf')}
          </button>
          <button type="button" disabled={p.busy} onClick={p.onPrint} data-testid="shift-report-print" className="flex-1 min-w-[140px] bg-primary-600 hover:bg-primary-500 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2 shadow-lg shadow-primary-500/10">
            <Printer size={16} />
            {t('mt.rep_print')}
          </button>
          <button type="button" onClick={p.onClose} className="flex-1 min-w-[120px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-[#161b22] dark:text-[#8b949e] py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all">
            {t('mt.rep_close')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Daily report (3 pages)
// ---------------------------------------------------------------------------------------------------------------------

export interface DayReportData {
  readonly dayNo: string;
  readonly date: string;
  readonly closed: boolean;
  readonly officeName: string;
  readonly generatedLabel: string;
  readonly rows: readonly ReportRow[];
  readonly expected: { readonly iqd: string; readonly usd: string };
  readonly actual: { readonly iqd: string; readonly usd: string } | null;
  readonly diff: { readonly zero: boolean; readonly iqdSurplus: boolean; readonly iqd: string; readonly usdSurplus: boolean; readonly usd: string };
  readonly operationsCount: number;
  readonly expensesCount: number;
  readonly note: string | null;
  readonly counts: readonly ReportCountLine[];
  readonly operations: readonly ReportOperation[];
  readonly expenses: readonly ReportOperation[];
  readonly copy: boolean;
}

export function DayReportPages({ d }: { d: DayReportData }) {
  const { t, locale } = useI18n();
  const footer = (pageKey: string) => (
    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400 flex justify-between items-center font-sans">
      <span>{d.officeName}</span>
      <span>{t(pageKey)}</span>
    </div>
  );
  return (
    <div className="space-y-6" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {/* PAGE 1: Comparison Table & Mathematical Summary */}
      <div className="printable-report flex flex-col justify-between">
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-6 text-white text-right space-y-2 shadow-xl shadow-blue-500/10 print:border print:border-black print:bg-none print:text-black">
            <div className="flex justify-between items-center">
              <span className="px-3 py-1 rounded-full bg-white/20 text-[10px] font-black uppercase tracking-wider print:border print:border-black print:text-black">{d.closed ? t('mt.drep_closed') : t('mt.drep_open')}</span>
              <h1 className="text-xl font-extrabold font-sans">{t('mt.drep_sheet')}</h1>
            </div>
            <div className="pt-2 border-t border-white/10 flex justify-between items-end text-xs">
              <div className="text-left">
                <p className="opacity-80">{t('mt.drep_code')}</p>
                <p className="text-lg font-black font-mono">#{d.dayNo}</p>
              </div>
              <div className="text-right">
                <p className="opacity-80">{t('mt.drep_date')}</p>
                <p className="text-lg font-black font-mono">{d.date}</p>
              </div>
            </div>
          </div>
          <div className="text-center pb-4 border-b border-dashed border-slate-200 dark:border-[#30363d] space-y-1">
            <h2 className="text-2xl font-black text-primary-600 dark:text-primary-400 tracking-wider">{d.officeName}</h2>
            {d.copy && <p className="text-xs font-black text-amber-600">{t('mt.rep_copy')}</p>}
            <p className="text-xs text-slate-500 font-bold font-mono pt-2">
              {t('mt.drep_time')} {d.generatedLabel}
            </p>
          </div>
          <h4 className="text-xs font-black uppercase text-slate-400 dark:text-slate-500 tracking-wider mb-1 text-right">{t('mt.drep_grid_title')}</h4>
          <div className="overflow-hidden border border-slate-200 dark:border-[#30363d] rounded-2xl">
            <table className="w-full text-right border-collapse text-[11px]">
              <thead>
                <tr className="bg-slate-50 dark:bg-[#161b22] text-slate-500 dark:text-[#8b949e] border-b border-slate-200 dark:border-[#30363d] font-black uppercase tracking-widest">
                  <th className="p-3 text-right">{t('mt.drep_col_account')}</th>
                  <th className="p-3 text-center">{t('mt.drep_col_opening')}</th>
                  <th className="p-3 text-center">{t('mt.drep_col_closing')}</th>
                  <th className="p-3 text-left">{t('mt.drep_col_delta')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-[#30363d] font-mono">
                {d.rows.map((r) => (
                  <tr key={r.key} className={`hover:bg-slate-50/50 dark:hover:bg-[#161b22]/10 ${r.rowCls ?? ''}`} data-testid="report-row">
                    <td className={`p-3 text-right font-sans font-bold ${r.nameCls ?? 'text-slate-800 dark:text-slate-200'}`}>{r.name}</td>
                    <td className="p-3 text-center text-slate-500 dark:text-slate-400">{cells(r.opening)}</td>
                    <td className="p-3 text-center text-slate-900 dark:text-white font-black">{cells(r.closing)}</td>
                    <td className="p-3 text-left font-black">{cells(r.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-xl border border-slate-100 dark:border-[#30363d] text-right">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{t('mt.drep_expected')}</p>
              <p className="text-lg font-black font-mono text-slate-800 dark:text-white">{d.expected.iqd} د.ع</p>
              <p className="text-sm font-black font-mono text-blue-600">${d.expected.usd}</p>
            </div>
            {d.actual && (
              <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-xl border border-slate-100 dark:border-[#30363d] text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{t('mt.drep_actual')}</p>
                <p className="text-lg font-black font-mono text-primary-600">{d.actual.iqd} د.ع</p>
                <p className="text-sm font-black font-mono text-blue-600">${d.actual.usd}</p>
              </div>
            )}
          </div>
          {d.closed ? (
            <div className={`p-4 rounded-xl text-center font-black ${d.diff.zero ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600' : 'bg-rose-50 dark:bg-rose-950/20 text-rose-600'}`}>
              <p className="uppercase text-[10px] tracking-widest text-[#8b949e] mb-1">{t('mt.drep_total_diff')}</p>
              {d.diff.zero ? (
                <p className="text-sm">{t('mt.drep_perfect')}</p>
              ) : (
                <div className="space-y-1 font-mono text-sm leading-tight text-center flex flex-col items-center">
                  <p>
                    {d.diff.iqdSurplus ? `${t('mt.drep_iqd_surplus')} ` : `${t('mt.drep_iqd_deficit')} `}
                    {d.diff.iqd} د.ع
                  </p>
                  <p>
                    {d.diff.usdSurplus ? `${t('mt.drep_usd_surplus')} ` : `${t('mt.drep_usd_deficit')} `}
                    {d.diff.usd}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl text-center font-black text-blue-600">
              <p className="uppercase text-[10px] tracking-widest text-[#8b949e] mb-1">{t('mt.drep_status')}</p>
              <p className="text-sm">{t('mt.drep_ongoing')}</p>
            </div>
          )}
          {countTable(d.counts, t)}
          <div className="grid grid-cols-2 gap-4 text-[11px] pt-2 text-slate-500 dark:text-slate-400 font-sans text-right">
            <div className="text-left font-sans">
              <p>
                {t('mt.drep_moves_count')} <strong className="font-mono text-slate-850 dark:text-white">{d.operationsCount}</strong>
              </p>
            </div>
            <div className="text-right font-sans">
              <p>
                {t('mt.drep_expenses_count')} <strong className="font-mono text-slate-850 dark:text-white">{d.expensesCount}</strong>
              </p>
            </div>
          </div>
        </div>
        {d.note && (
          <div className="text-xs italic text-slate-400 bg-slate-50 dark:bg-[#06080b] p-3 rounded-xl border border-slate-100/50 dark:border-[#30363d]/50 text-right mt-4 print:border-black">
            {t('mt.drep_note')} "{d.note}"
          </div>
        )}
        {footer('mt.drep_page1')}
      </div>
      <div className="page-break my-6" />
      {/* PAGE 2: Attached Transactions Report */}
      <div className="printable-report flex flex-col justify-between">
        <div className="space-y-6">
          <div className="text-center pb-4 border-b-2 border-dashed border-slate-200 dark:border-[#30363d] space-y-1">
            <h2 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">{t('mt.drep_appendix')}</h2>
            <p className="text-xs text-slate-500 font-bold font-mono">
              #{d.dayNo} | {d.date}
            </p>
          </div>
          <OperationsTable ops={d.operations} />
        </div>
        {footer('mt.drep_page2')}
      </div>
      <div className="page-break my-6" />
      {/* PAGE 3: Attached Expenses Report */}
      <div className="printable-report flex flex-col justify-between">
        <div className="space-y-6">
          <OperationsTable ops={d.expenses} title={t('mt.day_expenses')} />
        </div>
        {footer('mt.drep_page3')}
      </div>
    </div>
  );
}

export function DayReportModal(p: ReportWindowProps & { readonly data: DayReportData | null; readonly allowed: boolean }) {
  const { t } = useI18n();
  if (!p.open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="day-report-modal">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border border-slate-100 dark:border-[#30363d] w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="p-6 border-b border-slate-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#161b22]/30 animate-fadeIn overflow-hidden shrink-0">
          <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">{t('mt.drep_title')}</h3>
          <button type="button" onClick={p.onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar p-8 space-y-6 multi-page-container bg-slate-50 dark:bg-slate-900">
          {!p.allowed ? (
            <div className="printable-report flex flex-col justify-between p-8">
              <div className="p-8 bg-amber-50 dark:bg-amber-950/20 rounded-3xl border-2 border-dashed border-amber-200 dark:border-amber-800/40 text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-white dark:bg-[#161b22] flex items-center justify-center mx-auto text-amber-500 shadow-md">
                  <Shield size={24} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-800 dark:text-white">{t('mt.drep_admin_required')}</h4>
                  <p className="text-xs text-slate-500 dark:text-[#8b949e]">{t('mt.drep_admin_text')}</p>
                </div>
              </div>
            </div>
          ) : (
            p.data && <DayReportPages d={p.data} />
          )}
        </div>
        {p.allowed && p.data && (
          <div className="p-5 border-t border-slate-100 dark:border-[#30363d] flex flex-wrap gap-3 bg-gray-50/50 dark:bg-[#161b22]/30 shrink-0">
            <button type="button" disabled={p.busy} onClick={p.onPdf} className="flex-1 min-w-[140px] bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2 shadow-lg shadow-rose-500/10">
              <Download size={16} />
              {t('mt.drep_export_pdf')}
            </button>
            <button type="button" disabled={p.busy} onClick={p.onPrint} data-testid="day-report-print" className="flex-1 min-w-[140px] bg-primary-600 hover:bg-primary-500 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2 shadow-lg shadow-primary-500/10">
              <Printer size={16} />
              {t('mt.drep_print')}
            </button>
            <button type="button" onClick={p.onClose} className="flex-1 min-w-[120px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-[#161b22] dark:text-[#8b949e] py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all">
              {t('mt.rep_close')}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
